// 이미 저장된 의뢰서 ↔ 환자 명부 연결 (2026-10-17 점검 P1-1, 대표 "권장대로")
//   종전에는 「새 의뢰서 작성 순간」(CreateOrder [명부에서 선택])에만 연결할 수 있었다 — 앱 등록 전에 보낸 의뢰서·연결 실패한 의뢰서·덴클 앱으로 쓴 의뢰서는 끝까지 이어지지 않았다.
//   여기서 두 진입점을 제공한다. 서버는 기존 linkOrderToPatient 그대로(작성·수정 구분 없이 workOrders.fromUserId == 이 치과 · 레코드 active · 필수 동의만 검사).
//   ① LinkOrderPickerModal — 환자 상세 [의뢰서 연결]: 서버 listUnlinkedClinicOrders(검수 반영 10-17 — workOrders 직접 조회 폐지)로 아직 환자 연결 안 된 의뢰서(같은 이름 우선)
//   ② OrderPatientLink(default) — 의뢰서 보기(ViewOrder) [환자 연결]: 명부에서 환자 선택(ClinicPatientPicker 재사용)
//   앱 등록 환자(덴클 케어)는 CreateOrder 와 같은 L-6 확인 창을 거친다(정본 v2 §9 L-6 — 이름·생년월일·휴대폰 끝 4자리 대조, 확인 시각을 appLinkCheck 로 서버 접속기록에 남김).
//   ⚠️ 환자명은 화면(치과 자기 명부·자기 의뢰서)에만 — console 로그에 남기지 않는다.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Link2 } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import { linkOrderToPatient, listUnlinkedClinicOrders, describeError, isAppRegisteredRecord, normalizePatientQuery } from '../../services/clinicPatientsApi';
import { buildAppLinkCheck } from '../../services/careAppApi';
import { getFeatures, isClinicPatientsOn } from '../../utils/features';
import ClinicPatientPicker from './ClinicPatientPicker';

const LIST_LIMIT = 100;   // 서버 limit 상한(≤100)
// 조회 기간 — 서버 days(1~730, 기본 180)
const PERIODS = [{ days: 180, label: '최근 180일' }, { days: 365, label: '최근 1년' }, { days: 730, label: '최근 2년' }];
// 의뢰서 상태 → 짧은 라벨(서버가 반려·취소·임시저장은 이미 뺌). 모르는 값은 표시하지 않는다(지어내지 않음)
const ORDER_STATUS_LABEL = { pending: '접수', accepted: '제작 중', in_progress: '제작 중', completed: '완료' };
// 연결 불가 상태(서버 linkOrderToPatient 'order-not-linkable' 와 같은 기준) — ViewOrder [환자 연결] 숨김
export const NOT_LINKABLE_STATUS = ['cancelled', 'canceled', 'rejected', 'draft'];

const fmtDate = (v) => {
    if (!v) return '';
    const d = typeof v === 'number' ? new Date(v) : (v.toDate ? v.toDate() : (v._seconds ? new Date(v._seconds * 1000) : new Date(v)));
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

// 연결 오류 문구 — 'consent-required' 는 등록 화면용 문구("필수 동의 항목을 모두 확인해 주세요")가 연결 상황과 맞지 않아 여기서 바꾼다
export function describeLinkError(e) {
    if (e?.code === 'functions/failed-precondition' && e?.details?.code === 'consent-required') {
        console.error('[환자 연결] 실패:', e?.code || '', e?.details?.code || '');
        return '수집·민감정보 동의가 확인되지 않은 환자라 의뢰서를 연결할 수 없습니다.';
    }
    if (e?.code === 'functions/failed-precondition' && e?.details?.code === 'order-not-linkable') {
        console.error('[환자 연결] 실패:', e?.code || '', e?.details?.code || '');
        return '반려·취소되었거나 임시 저장 중인 의뢰서는 환자에게 연결할 수 없습니다.';
    }
    return describeError(e, '연결하지 못했습니다. 잠시 후 다시 시도해 주세요.');
}

// 연결 확인 창 — 앱 등록 환자는 L-6 문구(CreateOrder 와 같은 문장), 그 외는 간단 확인. 의뢰서 환자명과 명부 이름이 다르면 경고를 덧붙인다.
function LinkConfirmDialog({ rec, orderPatientName, busy, error, onCancel, onConfirm }) {
    const app = isAppRegisteredRecord(rec);
    const nameDiffers = !!orderPatientName && normalizePatientQuery(orderPatientName) !== normalizePatientQuery(rec?.name);
    return (
        <div role="dialog" aria-modal="true"
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: '18px 18px 14px' }}>
                <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.7 }}>
                    {app
                        ? `${rec?.name || '(이름 없음)'} 님은 덴클 케어 앱에 등록된 환자입니다. 연결하면 이 의뢰서의 보철물 제작 과정이 환자 앱에 표시됩니다. 생년월일 ${rec?.birthDate || (rec?.birthYear ? `${rec.birthYear}년` : '(없음)')}·휴대폰 끝 4자리 ${rec?.phoneLast4 || '(없음)'}가 우리 환자 기록과 같은지 확인해 주세요.`
                        : `이 의뢰서를 ${rec?.name || '(이름 없음)'} 환자에게 연결할까요? 연결하면 환자 상세의 보철물 제작 기록에 표시됩니다.`}
                </div>
                {nameDiffers && (
                    <div style={{ marginTop: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5 }}>
                        {`의뢰서의 환자명(${orderPatientName})과 명부 이름(${rec?.name || '(이름 없음)'})이 다릅니다. 같은 환자가 맞는지 다시 확인해 주세요.`}
                    </div>
                )}
                {error && <div style={{ marginTop: 10, background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                    <button type="button" disabled={busy} onClick={onCancel}
                        style={{ padding: '8px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontSize: 13, cursor: 'pointer' }}>{'취소'}</button>
                    <button type="button" disabled={busy} onClick={onConfirm}
                        style={{ padding: '8px 14px', border: 'none', borderRadius: 8, background: '#4F46E5', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.7 : 1 }}>
                        {busy ? '연결 중...' : (app ? '확인하고 연결' : '연결')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// 연결 실행 공통 — 앱 등록 환자는 확인 창 [확인하고 연결] 시각을 appLinkCheck 로 보낸다(서버 3시간 창 — 누른 즉시 호출이라 항상 유효)
async function doLink(orderId, rec) {
    const extra = isAppRegisteredRecord(rec) ? { appLinkCheck: buildAppLinkCheck(Date.now()) } : undefined;
    return linkOrderToPatient(orderId, rec.pid || rec.id, extra);
}

/**
 * ① 환자 상세 [의뢰서 연결] — 이 치과의 최근 의뢰서(최대 100건) 중 환자 미연결·의뢰서(메시지 제외)·목록에서 숨기지 않은 것.
 *   같은 이름 의뢰서를 위로. props: clinicId, record(상세 응답 record — pid·name·birthDate·phoneLast4·source), onClose, onLinked
 */
export function LinkOrderPickerModal({ clinicId, record, onClose, onLinked }) {
    const [rows, setRows] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [term, setTerm] = useState('');
    const [days, setDays] = useState(PERIODS[0].days);
    const [confirmOrder, setConfirmOrder] = useState(null);
    const [busy, setBusy] = useState(false);
    const [linkError, setLinkError] = useState('');

    // 서버 조회 — 기간·이름 검색어가 바뀌면 다시(검색어는 400ms 디바운스). 열람마다 서버 접속기록(건수만, 검색어 미기록)
    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        const t = setTimeout(async () => {
            try {
                const r = await listUnlinkedClinicOrders({ days, limit: LIST_LIMIT, nameQuery: term });
                if (!alive) return;
                setRows(Array.isArray(r?.items) ? r.items : []);
                setTruncated(r?.truncated === true);
            } catch (e) {
                if (!alive) return;
                console.error('[환자 연결] 미연결 의뢰서 조회 실패:', e?.code || '', e?.details?.code || '', e?.message || '');
                setRows([]); setTruncated(false);
                // 서버 함수 배포 전: 호출 대상 없음(not-found) 또는 internal — 원인을 화면에 알린다(조용한 실패 금지)
                if (e?.code === 'functions/not-found') setError('서버 업데이트 전이라 아직 의뢰서 목록을 볼 수 없습니다. 업데이트 후 다시 열어 주세요.');
                else if (e?.code === 'functions/internal') setError('의뢰서 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요. (계속되면 서버 업데이트 전일 수 있습니다 — 덴탈클러스터에 알려 주세요)');
                else setError(describeError(e, '의뢰서 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
            } finally { if (alive) setLoading(false); }
        }, term ? 400 : 0);
        return () => { alive = false; clearTimeout(t); };
    }, [clinicId, days, term]);

    const myKey = normalizePatientQuery(record?.name);
    const filtered = useMemo(() => {
        const base = rows;   // 이름 검색은 서버 nameQuery 로 이미 걸러짐
        // 같은 이름 먼저(안정 정렬 — 그 안에서는 작성일 내림차순 유지)
        const same = base.filter(o => myKey && normalizePatientQuery(o.patientName) === myKey);
        const other = base.filter(o => !(myKey && normalizePatientQuery(o.patientName) === myKey));
        return [...same, ...other];
    }, [rows, myKey]);

    const confirm = async () => {
        if (!confirmOrder || busy) return;
        setBusy(true); setLinkError('');
        try {
            const r = await doLink(confirmOrder.orderId, record);
            setConfirmOrder(null);
            onLinked && onLinked(r);
        } catch (e) {
            setLinkError(describeLinkError(e));
        } finally { setBusy(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ ...U.card, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{`${record?.name || '환자'} — 의뢰서 연결`}</h3>
                    <button type="button" onClick={onClose} style={{ ...U.buttonSecondary, padding: '4px 8px' }}><X size={14} /></button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{'아직 환자와 연결되지 않은 의뢰서입니다(반려·취소·임시 저장 제외). 같은 이름의 의뢰서가 위에 표시됩니다.'}</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    {PERIODS.map(pd => (
                        <button key={pd.days} type="button" onClick={() => setDays(pd.days)}
                            style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, background: days === pd.days ? '#4f46e5' : '#fff', color: days === pd.days ? '#fff' : '#334155', borderColor: days === pd.days ? '#4f46e5' : '#e2e8f0' }}>{pd.label}</button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                    <Search size={14} color="#94a3b8" />
                    <input autoFocus value={term} maxLength={20} onChange={(e) => setTerm(e.target.value)} placeholder="환자명"
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
                </div>
                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{error}</div>}
                <div style={{ overflow: 'auto', flex: 1 }}>
                    {loading ? <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>{'불러오는 중...'}</div>
                        : filtered.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{term.trim() ? '검색 결과가 없습니다.' : '연결할 수 있는 의뢰서가 없습니다.'}</div>
                            : filtered.map(o => {
                                const same = myKey && normalizePatientQuery(o.patientName) === myKey;
                                return (
                                    <button key={o.orderId} type="button" onClick={() => { setLinkError(''); setConfirmOrder(o); }}
                                        style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 8px', border: 'none', borderBottom: '1px solid #f1f5f9', background: same ? '#f0fdf4' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{o.patientName || '(환자명 없음)'}</span>
                                            {same && <span style={{ ...U.badge, background: '#dcfce7', color: '#166534', fontSize: 11 }}>{'같은 이름'}</span>}
                                            {ORDER_STATUS_LABEL[o.status] && <span style={{ ...U.badge, background: '#f1f5f9', color: '#475569', fontSize: 11 }}>{ORDER_STATUS_LABEL[o.status]}</span>}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                            {[fmtDate(o.createdAt), o.labName || '', o.orderNumber || ''].filter(Boolean).join(' · ')}
                                        </span>
                                    </button>
                                );
                            })}
                </div>
                {truncated && !loading && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
                        {days < PERIODS[PERIODS.length - 1].days ? '최근 의뢰서까지만 찾았습니다. 더 오래된 의뢰서는 위에서 기간을 늘려 찾아 주세요.' : '최근 의뢰서까지만 찾았습니다. 환자명으로 검색해 범위를 좁혀 주세요.'}
                    </div>
                )}
            </div>
            {confirmOrder && (
                <LinkConfirmDialog rec={record} orderPatientName={confirmOrder.patientName || ''} busy={busy} error={linkError}
                    onCancel={() => { setConfirmOrder(null); setLinkError(''); }} onConfirm={confirm} />
            )}
        </div>
    );
}

/**
 * ② 의뢰서 보기(ViewOrder) [환자 연결] — 발신 치과 화면 전용. 게이트: 치과 계정 && isClinicPatientsOn(플래그 또는 베타).
 *   이미 연결된 의뢰서면 「환자 명부에 연결됨」 + [환자 보기](해제는 환자 상세에서 — 기존 경로). 메시지 문서는 표시 안 함. 인쇄 제외(no-print)
 */
function OrderPatientLink({ order, user }) {
    const navigate = useNavigate();
    const [on, setOn] = useState(false);
    const [picker, setPicker] = useState(false);
    const [rec, setRec] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [linkedPid, setLinkedPid] = useState(null);   // 이 화면에서 방금 연결한 pid

    // 치과 판정 — App.js /call 라우트와 같은 방식(businessType·companyBusinessType 각각 검사: 대표 businessType 이 비어 있지 않은 직원 계정도 회사 유형으로 판정)
    const CLINIC_TYPES = ['clinic', 'dental', '치과'];
    const isClinicAccount = CLINIC_TYPES.includes(user?.businessType) || CLINIC_TYPES.includes(user?.companyBusinessType);
    useEffect(() => {
        let alive = true;
        if (!isClinicAccount) { setOn(false); return () => { alive = false; }; }
        getFeatures().then(f => { if (alive) setOn(isClinicPatientsOn(f, user, user?.email)); })
            .catch((e) => { console.error('[환자 연결] 플래그 조회 실패:', e?.message || e); if (alive) setOn(false); });
        return () => { alive = false; };
        // ViewOrderWrapper 가 user 객체를 렌더마다 새로 만들므로 판정에 쓰는 값만 의존(베타 판정 = uid·companyId·email)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClinicAccount, user?.uid, user?.companyId, user?.email]);

    if (!on || !order?.id || order.type === 'message') return null;
    // 반려·취소·임시 저장 의뢰서는 연결 버튼을 두지 않는다(서버도 'order-not-linkable' 로 거부 예정). 이미 연결된 경우는 아래 「연결됨」 표시 유지
    const notLinkable = NOT_LINKABLE_STATUS.includes(order.status);
    const clinicId = user?.companyId || user?.uid;
    // patientRecordId = 'clinicId/pid' (서버 linkOrderToPatient 기록)
    const pidFromOrder = typeof order.patientRecordId === 'string' ? order.patientRecordId.split('/')[1] : null;
    const pid = linkedPid || pidFromOrder;

    const confirm = async () => {
        if (!rec || busy) return;
        setBusy(true); setErr('');
        try {
            await doLink(order.id, rec);
            setLinkedPid(rec.id);
            setRec(null);
        } catch (e) {
            setErr(describeLinkError(e));
        } finally { setBusy(false); }
    };

    if (pid) {
        return (
            <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: '#3730a3', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '6px 10px', margin: '8px 0' }}>
                <span>{'환자 명부에 연결된 의뢰서입니다. 연결 해제는 환자 상세 화면에서 할 수 있습니다.'}</span>
                <button type="button" onClick={() => navigate(`/patients/${pid}`)}
                    style={{ marginLeft: 'auto', border: '1px solid #c7d2fe', background: '#fff', color: '#3730a3', borderRadius: 6, padding: '2px 9px', fontSize: 11.5, cursor: 'pointer' }}>{'환자 보기'}</button>
            </div>
        );
    }

    if (notLinkable) return null;

    return (
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', margin: '8px 0' }}>
            <span>{'환자 명부와 연결되지 않은 의뢰서입니다.'}</span>
            <button type="button" onClick={() => { setErr(''); setPicker(true); }}
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #c7d2fe', background: '#fff', color: '#4338ca', borderRadius: 6, padding: '2px 9px', fontSize: 11.5, cursor: 'pointer' }}><Link2 size={12} /> {'환자 연결'}</button>
            {err && !rec && <div style={{ width: '100%', color: '#b91c1c' }}>{err}</div>}
            {picker && (
                <ClinicPatientPicker clinicId={clinicId} initialTerm={order.patientName || ''}
                    onClose={() => setPicker(false)}
                    onSelect={(r) => { setPicker(false); setErr(''); setRec(r); }} />
            )}
            {rec && (
                <LinkConfirmDialog rec={rec} orderPatientName={order.patientName || ''} busy={busy} error={err}
                    onCancel={() => { setRec(null); setErr(''); }} onConfirm={confirm} />
            )}
        </div>
    );
}

export default OrderPatientLink;
